import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Button,
  Alert,
  Paper,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Fab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChildCareOutlinedIcon from "@mui/icons-material/ChildCareOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageContainer from "../components/PageContainer";
import UserManagementSkeleton from "../components/UserManagement/UserManagementSkeleton";
import {
  ManageUserCircles,
  ManageUserFamily,
  DeleteConfirmationDialog,
} from "../components/UserManagement";
import { useParentFamilyData } from "../hooks/useParentFamilyData";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { NoticeboardButton } from "../components/shared";
import CreationWizard from "../components/CreationWizard/CreationWizard";
import { useUsers } from "../hooks/useUsers";
import { useUserCircles } from "../hooks/useCircleData";
import { useRemoveUser } from "../hooks/useRemoveUser";
import { useAuth } from "../context/AuthContext";
import { User } from "../services/api";
import { USER_MANAGEMENT } from "../routes";
import { readUserHomeHandover } from "../domain/users/usersHandover";
import { getAvatarColor, getInitials } from "../utils/avatarUtils";
import {
  formatUserEmailDisplay,
  isRealUserEmail,
} from "../utils/userContactUtils";
import { lightTheme } from "../styles/theme";
import {
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../styles/managementShared";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";

const userHeroBackground = (primaryMain: string) =>
  `radial-gradient(circle at 85% 15%, ${alpha(primaryMain, 0.12)} 0%, transparent 45%), linear-gradient(135deg, ${lightTheme.colors.gradientHeroSurface[0]} 0%, ${lightTheme.colors.gradientHeroSurface[1]} 100%)`;

const UserHomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileFabVisible = useMobileFabVisibility(isMobile);
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, token } = useAuth();
  const orgId = authUser?.schoolId || authUser?.orgId || "";

  const { data: users = [], isLoading } = useUsers();
  const user = useMemo(
    () => users.find((u) => u.id === userId),
    [users, userId]
  );

  const {
    userCircles,
    isLoading: userCirclesLoading,
    refetch: refetchUserCircles,
  } = useUserCircles(userId ?? "");

  const subcircleCount = userCircles.length;

  // Stable object references for ManageUserCircles's props — it's wrapped in
  // React.memo, but a fresh object literal here every render would defeat
  // that memo every time (this page re-renders on every mobile scroll tick
  // via useMobileFabVisibility above).
  const manageUserCirclesUser = useMemo(
    () =>
      user && {
        id: user.id,
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber,
      },
    [user]
  );

  const manageUserCirclesData = useMemo(
    () => ({
      userCircles,
      isLoading: userCirclesLoading,
      error: null,
      refetch: refetchUserCircles,
    }),
    [userCircles, userCirclesLoading, refetchUserCircles]
  );

  const { isFamilyManagementEnabled } = useFeatureFlags();

  const { stats: familyStats, isHydrated: familyHydrated } = useParentFamilyData(
    orgId,
    userId ?? "",
    isFamilyManagementEnabled
  );

  const userCircleOptions = useMemo(
    () =>
      userCircles.map((circle) => ({
        circleId: circle.circleId,
        name: circle.name,
        alias: circle.alias,
      })),
    [userCircles]
  );

  const [directNoticeOpen, setDirectNoticeOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Another page (Ask Noticeboard) can open a User with Remove already asked.
  // Read once the User has loaded, then cleared, so a reload or Back never opens it again.
  useEffect(() => {
    if (!user || !readUserHomeHandover(location.state).remove) return;
    setUserToDelete(user);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [user, location.state, location.pathname, location.search, navigate]);

  const { removeUser, isRemoving } = useRemoveUser({
    schoolId: orgId,
    token: token || "",
    onSuccess: () => {
      setUserToDelete(null);
      navigate(USER_MANAGEMENT);
    },
  });

  const joinedLabel = user
    ? new Date(user.joinedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

  const contactLine = useMemo(() => {
    if (!user) return "";
    const parts: string[] = [];
    if (isRealUserEmail(user.email)) {
      parts.push(formatUserEmailDisplay(user.email));
    }
    if (user.phoneNumber?.trim()) {
      parts.push(user.phoneNumber.trim());
    }
    return parts.join(" · ");
  }, [user]);

  const handleConfirmRemove = useCallback(
    (id: string) => {
      removeUser(id);
    },
    [removeUser]
  );

  const handleCirclesChanged = useCallback(() => {
    void refetchUserCircles();
  }, [refetchUserCircles]);

  if (isLoading) {
    return (
      <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
        <UserManagementSkeleton />
      </PageContainer>
    );
  }

  if (!userId || !user) {
    return (
      <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This user was not found or is no longer in your organization.
        </Alert>
        <Button variant="contained" onClick={() => navigate(USER_MANAGEMENT)}>
          Back to Users
        </Button>
      </PageContainer>
    );
  }

  const avatarSx = {
    bgcolor: getAvatarColor(user.name, lightTheme),
    color: "common.white",
    fontWeight: 700,
  };

  return (
    <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
      <Box
        sx={{
          maxWidth: { xs: 1200, lg: "none" },
          mx: "auto",
          width: "100%",
        }}
      >
        <Box sx={{ mt: 1, mb: 2 }}>
          <Breadcrumbs
            separator={
              <ChevronRightIcon
                sx={{
                  fontSize: { xs: 16, sm: 17, md: 18 },
                  color: "text.secondary",
                  display: "block",
                }}
              />
            }
            aria-label="breadcrumb"
            sx={{
              maxWidth: "100%",
              "& .MuiBreadcrumbs-ol": {
                flexWrap: { xs: "nowrap", md: "wrap" },
                alignItems: "center",
              },
              "& .MuiBreadcrumbs-li:last-of-type": {
                minWidth: 0,
                overflow: "hidden",
              },
            }}
          >
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/")}
              sx={{ display: "inline-flex", alignItems: "center" }}
            >
              <HomeOutlinedIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </MuiLink>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(USER_MANAGEMENT)}
              sx={{ fontSize: { xs: "0.8125rem", md: "0.875rem" } }}
            >
              Users
            </MuiLink>
            <Typography
              color="text.primary"
              noWrap
              title={user.name}
              sx={{ fontSize: { xs: "0.8125rem", md: "0.875rem" } }}
            >
              {user.name}
            </Typography>
          </Breadcrumbs>
        </Box>

        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
            borderColor: alpha(theme.palette.primary.main, 0.14),
            background: userHeroBackground(theme.palette.primary.main),
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 1.5, md: 2 },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.5, md: 2 }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={{ xs: 1.5, md: 2 }}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Avatar
                  sx={{
                    ...avatarSx,
                    width: { xs: 48, md: 64 },
                    height: { xs: 48, md: 64 },
                    border: "2px solid",
                    borderColor: "background.paper",
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    flexShrink: 0,
                    boxShadow: theme.shadows[2],
                  }}
                >
                  {getInitials(user.name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    component="h1"
                    noWrap
                    title={user.name}
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1.125rem", md: "1.375rem" },
                      lineHeight: 1.25,
                      color: "text.primary",
                      mb: 0.25,
                    }}
                  >
                    {user.name}
                  </Typography>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    columnGap={0.75}
                    rowGap={0.25}
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "0.75rem", md: "0.8125rem" },
                      lineHeight: 1.35,
                    }}
                  >
                    {contactLine ? <span>{contactLine}</span> : null}
                    {contactLine ? (
                      <Typography
                        component="span"
                        sx={{ color: "text.disabled" }}
                      >
                        •
                      </Typography>
                    ) : null}
                    <Stack direction="row" alignItems="center" spacing={0.35}>
                      <GroupsOutlinedIcon sx={{ fontSize: 15 }} />
                      <span>
                        {userCirclesLoading
                          ? "…"
                          : `${subcircleCount} circle${subcircleCount !== 1 ? "s" : ""}`}
                      </span>
                    </Stack>
                    {isFamilyManagementEnabled ? (
                      <>
                        <Typography
                          component="span"
                          sx={{ color: "text.disabled" }}
                        >
                          •
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.35}>
                          <ChildCareOutlinedIcon sx={{ fontSize: 15 }} />
                          <span>
                            {!familyHydrated
                              ? "…"
                              : `${familyStats.childCount} family member${familyStats.childCount !== 1 ? "s" : ""}`}
                          </span>
                        </Stack>
                      </>
                    ) : null}
                    <Typography
                      component="span"
                      sx={{ color: "text.disabled" }}
                    >
                      •
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.35}>
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
                      <span>Joined {joinedLabel}</span>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexShrink: 0,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <NoticeboardButton
                  variant="primary"
                  size="small"
                  startIcon={<SendOutlinedIcon />}
                  onClick={() => setDirectNoticeOpen(true)}
                  sx={{ flex: { xs: 1, md: "none" }, minWidth: 0 }}
                >
                  Send Notice
                </NoticeboardButton>
                <NoticeboardButton
                  variant="danger"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setUserToDelete(user)}
                  sx={{ flex: { xs: 1, md: "none" }, minWidth: 0 }}
                >
                  Remove
                </NoticeboardButton>
              </Stack>
            </Stack>
          </Box>
        </Paper>

        <ManageUserFamily
          orgId={orgId}
          userId={user.id}
          userName={user.name}
          userCircles={userCircleOptions}
          familyEnabled={isFamilyManagementEnabled}
          circlesPanel={
            <ManageUserCircles
              layout={isFamilyManagementEnabled ? "embedded" : "page"}
              user={manageUserCirclesUser!}
              userCirclesData={manageUserCirclesData}
              onCirclesChanged={handleCirclesChanged}
            />
          }
        />

        <DeleteConfirmationDialog
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
          onConfirm={handleConfirmRemove}
          isRemoving={isRemoving}
        />

        {directNoticeOpen && (
          <CreationWizard
            key={user.id}
            noticeType="direct"
            open
            wizardSubtitle={`Only ${user.name} will see this notice in the app.`}
            initialFormData={{ targetUserId: user.id }}
            onClose={() => setDirectNoticeOpen(false)}
          />
        )}

        {isMobile && (
          <Fab
            color="primary"
            aria-label="Back to users"
            onClick={() => navigate(USER_MANAGEMENT)}
            sx={mobileFabWithScrollVisibility(
              MANAGEMENT_MOBILE_FAB_SX,
              mobileFabVisible
            )}
          >
            <ArrowBackIcon sx={{ fontSize: 28 }} />
          </Fab>
        )}
      </Box>
    </PageContainer>
  );
};

export default UserHomePage;
